import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { adminService } from "../../../../services/api/adminService";
import { normalizeEmployee } from "../../../../utils/employeeUtils";
import StaffTable from "./components/StaffTable";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import EmployeeDetail from "../../components/EmployeeDetail/EmployeeDetail";

const StaffList = () => {
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const search = searchParams.get("search") || "";
  const currentPage = Number(searchParams.get("page")) || 1;
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [localSearch, setLocalSearch] = useState(search);


  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.get_all_staff(
        search,
        currentPage,
        itemsPerPage,
        token,
      );
      setData(
        (response.data || []).map((emp, i) =>
          normalizeEmployee(emp, i, currentPage, itemsPerPage),
        ),
      );
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server.",
      );
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, itemsPerPage, token]);

  useEffect(() => {
    fetchData();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchData, user?.role]);

  const handleDelete = async (id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await adminService.delete_staff(deleteTarget, token);
      toast.success("Xóa tài khoản thành công!");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Không thể xóa tài khoản.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (localSearch) params.set("search", localSearch);
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") handleSearch();
  };

  const handleSubmitEmployee = async (payload, isAddExisting = false) => {
    try {
      if (editTarget) {
        await adminService.update_user(payload, editTarget, token);
        toast.success("Cập nhật tài khoản thành công!");
      } else {
        await adminService.create_user(payload, token);
        toast.success("Tạo tài khoản thành công!");
      }
      fetchData();
      setShowCreateModal(false);
      setEditTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Thao tác thất bại.");
      throw error;
    }
  };


  return (
    <>
      <div className="flex flex-col h-full min-h-0 gap-8 relative">
        <div className="shrink-0 flex justify-between items-center">
          <div className="relative w-1/3">
            <input
              type="text"
              placeholder="Tìm kiếm nhân viên"
              className="border border-gray-200 rounded-lg px-4 py-2 focus:outline-blue-500 w-full"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <span
              className="absolute right-3 top-2.5 cursor-pointer"
              onClick={handleSearch}
            >
              <img src="/icons/search-black.svg" alt="Search icon" />
            </span>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#0457C2] hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-be-vietnam-medium flex items-center gap-2"
          >
            <img src="/icons/user-plus.svg" alt="Add icon" />
            Tạo mới
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-visible">
          <div className="h-full min-h-0 overflow-auto p-[2px]">
            <StaffTable
              data={data}
              loading={loading}
              currentPage={currentPage}
              setCurrentPage={(page) => {
                const params = new URLSearchParams(searchParams);
                params.set("page", page.toString());
                setSearchParams(params);
              }}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setView={(item) => {
                if (!item._id) {
                  toast.error("Không tìm thấy account ID");
                  return;
                }
                navigate(`/danh-sach-tai-khoan/tai-khoan/${item._id}`);
              }}
              setEdit={true}
              handleEdit={(id, item) => {
                const staffId = item._id || id;
                if (!staffId) {
                  toast.error("Không tìm thấy account ID");
                  return;
                }
                setEditTarget(staffId);
              }}
              setDelete={canDelete}
              handleDelete={(id, item) => {
                handleDelete(item.staffId || item._id || id);
              }}
              basePath="/danh-sach-tai-khoan/tai-khoan"
            />
          </div>
        </div>
      </div>

      {showCreateModal && (
        <EmployeeDetail
          mode="create"
          layout="popup"
          isOpen={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          onSuccess={handleSubmitEmployee}
          title="Tạo nhân viên mới"
        />
      )}

      {editTarget && (
        <EmployeeDetail
          employeeId={editTarget}
          mode="edit"
          layout="popup"
          isOpen={!!editTarget}
          onCancel={() => setEditTarget(null)}
          onSuccess={handleSubmitEmployee}
          title="Chỉnh sửa thông tin nhân viên"
        />
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title="Xác nhận xóa tài khoản"
        message="Bạn có chắc chắn muốn xóa tài khoản này không?"
        loading={loading}
      />
    </>
  );
};

export default StaffList;
